import { NgModule } from '@angular/core';
import type { Routes } from '@angular/router';
import { RouterModule } from '@angular/router';

import { LIVE_PREVIEW_PATH, PREVIEW_PATH, PREVIEW_PRESET_PATH, WYSIWYG_PATH } from 'src/shared/constants/paths/paths';

import { LayoutComponent } from './layouts/layout.component';
import { GetMobileAppResolverService } from './resolvers/get-mobile-app-resolver.service';
import { KlaxoonResolverService } from './resolvers/klaxoon-resolver.service';
import { LivePreviewViewResolverService } from './resolvers/live-preview-view-resolver.service';
import { MicrositePostResolverService } from './resolvers/microsite-post-resolver.service';
import { NotFoundResolverService } from './resolvers/not-found-resolver.service';
import { PersonalisationResolverService } from './resolvers/personalisation-view-resolver.service';
import { PilotResolverService } from './resolvers/pilot-resolver.service';
import { PresetPreviewViewResolverService } from './resolvers/preset-preview-view-resolver.service';
import { PreviewViewResolverService } from './resolvers/preview-view-resolver.service';
import { QaaViewResolverService } from './resolvers/qaa-view-resolver.service';
import { SearchResolverService } from './resolvers/search-resolver.service';
import { ViewResolverService } from './resolvers/view-resolver.service';
import { WysiwygViewResolverService } from './resolvers/wysiwyg-view-resolver.service';
import { ErrorComponent } from './views/error/error.component';
import { PageViewComponent } from './views/page-view/page-view.component';

const routes: Routes = [
    {
        path: '',
        component: LayoutComponent,
        children: [
            {
                path: '',
                pathMatch: 'full',
                component: PageViewComponent,
                resolve: {
                    view: ViewResolverService,
                },
            },
            {
                path: '404',
                component: ErrorComponent,
                resolve: {
                    view: NotFoundResolverService,
                },
            },
            {
                path: PREVIEW_PRESET_PATH,
                pathMatch: 'full',
                resolve: {
                    view: PresetPreviewViewResolverService,
                },
                data: {
                    isPreview: true,
                },
                loadComponent: () =>
                    import('./views/preview-preset/preset-preview-view.component').then(
                        ({ PresetPreviewViewComponent }) => PresetPreviewViewComponent
                    ),
            },
            {
                path: PREVIEW_PATH,
                pathMatch: 'full',
                component: PageViewComponent,
                resolve: {
                    view: PreviewViewResolverService,
                },
                data: {
                    isPreview: true,
                },
            },
            {
                path: 'tools/qaa',
                pathMatch: 'full',
                component: PageViewComponent,
                resolve: {
                    view: QaaViewResolverService,
                },
                data: {
                    isPreview: true,
                },
            },
            {
                path: ':projectSlug/tools/qaa',
                pathMatch: 'full',
                component: PageViewComponent,
                resolve: {
                    view: QaaViewResolverService,
                },
                data: {
                    isPreview: true,
                },
            },
            {
                path: WYSIWYG_PATH,
                pathMatch: 'full',
                resolve: {
                    widgets: WysiwygViewResolverService,
                },
                loadComponent: () =>
                    import('./views/wysiwyg-view/wysiwyg-view.component').then(
                        ({ WysiwygViewComponent }) => WysiwygViewComponent
                    ),
            },
            {
                path: LIVE_PREVIEW_PATH,
                pathMatch: 'full',
                resolve: {
                    widgets: LivePreviewViewResolverService,
                },
                loadComponent: () =>
                    import('./views/live-preview-view/live-preview-view.component').then(
                        ({ LivePreviewViewComponent }) => LivePreviewViewComponent
                    ),
            },
            {
                path: 'get-mobile-app',
                component: PageViewComponent,
                resolve: {
                    view: GetMobileAppResolverService,
                },
            },
            {
                path: 'productivity/:subDir/:pageSlug',
                component: PageViewComponent,
                resolve: {
                    view: PersonalisationResolverService,
                },
            },
            {
                path: 'productivity/:pageSlug',
                component: PageViewComponent,
                resolve: {
                    view: PersonalisationResolverService,
                },
            },
            {
                path: ':projectSlug/:pageSlug',
                component: PageViewComponent,
                resolve: {
                    view: ViewResolverService,
                },
            },
            {
                path: ':projectSlug/faq/:pageTypeSlug/:pageSlug',
                component: PageViewComponent,
                resolve: {
                    view: ViewResolverService,
                },
            },
            {
                path: ':projectSlug/search/?query',
                component: PageViewComponent,
                resolve: {
                    view: SearchResolverService,
                },
            },
            {
                path: ':projectSlug/search/',
                component: PageViewComponent,
                resolve: {
                    view: SearchResolverService,
                },
            },
            {
                path: ':projectSlug/m/:postSlug',
                component: PageViewComponent,
                resolve: {
                    view: MicrositePostResolverService,
                },
            },
            {
                path: ':projectSlug/m/:subDir/:postSlug',
                component: PageViewComponent,
                resolve: {
                    view: MicrositePostResolverService,
                },
            },
            {
                path: ':projectSlug/:subDir/:pageSlug',
                component: PageViewComponent,
                resolve: {
                    view: ViewResolverService,
                },
            },
            {
                path: ':rootSlug',
                component: PageViewComponent,
                resolve: {
                    view: ViewResolverService,
                },
            },
            {
                path: '**',
                component: ErrorComponent,
                resolve: {
                    view: NotFoundResolverService,
                },
            },
        ],
    },
];

@NgModule({
    imports: [RouterModule.forChild(routes)],
    providers: [
        NotFoundResolverService,
        PreviewViewResolverService,
        PresetPreviewViewResolverService,
        QaaViewResolverService,
        ViewResolverService,
        GetMobileAppResolverService,
        MicrositePostResolverService,
        PilotResolverService,
        KlaxoonResolverService,
        PersonalisationResolverService,
    ],
})
export class TypicalProjectRoutingModule {}
